import { useMemo } from "react";

import {
  useAccessibleOrganizations,
  useNavigableWorkspaces,
  useUserOrganizations,
  useWorkspaces,
} from "src/api/queries";
import { WorkspaceDataplane } from "src/clients/flow-api";
import {
  OrganizationInDB,
  UserProvisioningMode,
} from "src/clients/taktile-api";
import { OrgWithWorkspaces } from "src/layout/types";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const useAssembledWorkspaceAndOrg = (
  rawOrgs: OrganizationInDB[] | undefined,
  workspaces: WorkspaceDataplane[] | undefined,
  isLoading: boolean,
) => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();

  const workspace = useMemo(
    () => workspaces?.find((ws) => ws.id === wsId) ?? null,
    [workspaces, wsId],
  );

  const orgs = useMemo(() => {
    if (!rawOrgs || !workspaces) {
      return [];
    }

    const workspacesByOrgId = workspaces.reduce(
      (acc, workspace) => {
        const orgWorkspaces: WorkspaceDataplane[] = acc[
          workspace.organization_id
        ]
          ? acc[workspace.organization_id]
          : [];
        return {
          ...acc,
          [workspace.organization_id]: [...orgWorkspaces, workspace],
        };
      },
      {} as Record<string, WorkspaceDataplane[]>,
    );

    return rawOrgs
      .map(
        (rawOrg) =>
          ({
            ...rawOrg,

            workspaces: (workspacesByOrgId[rawOrg.id] || []).sort((a, b) =>
              a.name.localeCompare(b.name),
            ),
          }) as OrgWithWorkspaces,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawOrgs, workspaces]);

  const org = useMemo(
    () => orgs.find((userOrg) => userOrg.id === orgId) || null,
    [orgs, orgId],
  );

  return { workspace, workspaces, org, orgs, isLoading };
};

export const useWorkspaceAndOrg = () => {
  const { data: rawOrgs, isLoading: isOrgsLoading } = useUserOrganizations();
  const { data: workspaces, isLoading: isWorkspacesLoading } = useWorkspaces();
  return useAssembledWorkspaceAndOrg(
    rawOrgs,
    workspaces,
    isOrgsLoading || isWorkspacesLoading,
  );
};

export const useNavigableWorkspaceAndOrg = () => {
  const scoped = isFeatureFlagEnabled(FEATURE_FLAGS.workspaceScopedTokens);

  const userOrgs = useUserOrganizations({ enabled: !scoped });
  const accessibleOrgs = useAccessibleOrganizations({ enabled: scoped });
  const scopedWorkspaces = useWorkspaces(false, false, { enabled: !scoped });
  const navigableWorkspaces = useNavigableWorkspaces({ enabled: scoped });

  const orgsQuery = scoped ? accessibleOrgs : userOrgs;
  const workspacesQuery = scoped ? navigableWorkspaces : scopedWorkspaces;

  return useAssembledWorkspaceAndOrg(
    orgsQuery.data,
    workspacesQuery.data,
    orgsQuery.isLoading || workspacesQuery.isLoading,
  );
};

export const useIsScimOnly = () => {
  const { org } = useWorkspaceAndOrg();
  return org?.user_provisioning_mode === UserProvisioningMode.SCIM_ONLY;
};
